import { useRef, useCallback } from "react";
import { Room, RoomEvent, Track } from "livekit-client";

interface KnowledgeReference {
  query: string;
  references: Array<{
    answerId: string;
    relevanceScore: number;
  }>;
  timestamp: number;
}

interface UseAvatarSessionProps {
  onStreamReady: (stream: MediaStream) => void;
  onAudioReady: (audioElement: HTMLAudioElement) => void;
  onKnowledgeReferences: (reference: KnowledgeReference) => void;
  onError: (message: string) => void;
  onDisconnect: () => void;
}

interface SessionData {
  livekit_url: string;
  room_token: string;
  session_id: string;
}

export function useAvatarSession({
  onStreamReady,
  onAudioReady,
  onKnowledgeReferences,
  onError,
  onDisconnect,
}: UseAvatarSessionProps) {
  const roomRef = useRef<Room | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  const connect = useCallback(
    async (sessionData: SessionData) => {
      try {
        // Create LiveKit room
        const room = new Room();
        roomRef.current = room;

        // Set up event listeners
        room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          console.log(
            "Track subscribed:",
            track.kind,
            "from",
            participant.identity
          );

          if (track.kind === Track.Kind.Video) {
            const videoTrack = track as any;
            videoTrackRef.current = videoTrack.mediaStreamTrack;

            // Create video stream
            const videoStream = new MediaStream([videoTrack.mediaStreamTrack]);
            onStreamReady(videoStream);
            console.log("Video track attached to video element");
          } else if (track.kind === Track.Kind.Audio) {
            const audioTrack = track as any;

            // Create separate audio element for audio playback
            const audioElement = document.createElement("audio");
            audioElement.srcObject = new MediaStream([
              audioTrack.mediaStreamTrack,
            ]);
            audioElement.autoplay = true;
            audioElement.style.display = "none";
            document.body.appendChild(audioElement);

            audioElementRef.current = audioElement;
            onAudioReady(audioElement);
            console.log("Audio track attached to audio element");
          }
        });

        // Handle browser autoplay restrictions
        room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
          if (!room.canPlaybackAudio) {
            console.log("Audio playback blocked - user interaction required");
            room
              .startAudio()
              .then(() => {
                console.log("Audio playback started successfully");
              })
              .catch((error) => {
                console.error("Failed to start audio playback:", error);
              });
          }
        });

        room.on(RoomEvent.Disconnected, (reason) => {
          console.log("Room disconnected:", reason);
          onDisconnect();
        });

        // Listen for data channel messages (knowledge references and errors)
        room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
          try {
            const decoder = new TextDecoder();
            const message = JSON.parse(decoder.decode(payload));

            if (message.type === "error") {
              // Log technical error details to console
              console.error("❌ Agent Error:", message.technicalMessage);
              console.error("Error type:", message.errorType);
              console.error(
                "Timestamp:",
                new Date(message.timestamp).toISOString()
              );

              // Show user-friendly error message
              onError(message.userMessage);
            } else if (message.type === "knowledge_references") {
              console.log("📚 Knowledge References:");
              console.log(`  Query: "${message.query}"`);
              console.log(`  Found ${message.references.length} references:`);
              message.references.forEach((ref: any, index: number) => {
                console.log(
                  `  ${index + 1}. Answer ${ref.answerId} (score: ${
                    ref.relevanceScore
                  })`
                );
              });

              // Add new knowledge reference
              onKnowledgeReferences({
                query: message.query,
                references: message.references,
                timestamp: message.timestamp || Date.now(),
              });
            }
          } catch (error) {
            console.error("Error parsing data channel message:", error);
          }
        });

        // Connect to LiveKit room
        await room.connect(sessionData.livekit_url, sessionData.room_token);

        // Enable microphone so the avatar can hear you
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
          console.log("Microphone enabled - avatar can now hear you");
        } catch (error) {
          console.error("Failed to enable microphone:", error);
          throw new Error(
            "Could not access your microphone. Please check your browser permissions."
          );
        }

        console.log("Connected to LiveAvatar session:", sessionData.session_id);
        return room;
      } catch (error) {
        console.error("Error starting avatar session:", error);
        throw error;
      }
    },
    [onStreamReady, onAudioReady, onKnowledgeReferences, onError, onDisconnect]
  );

  const disconnect = useCallback(async () => {
    if (!roomRef.current) {
      return;
    }

    try {
      // Clean up audio element
      if (audioElementRef.current) {
        audioElementRef.current.srcObject = null;
        audioElementRef.current.remove();
        audioElementRef.current = null;
      }

      await roomRef.current.disconnect();
      roomRef.current = null;
      videoTrackRef.current = null;
    } catch (error) {
      console.error("Error disconnecting session:", error);
    }
  }, []);

  const toggleMicrophone = useCallback(async (muted: boolean) => {
    if (!roomRef.current) return;

    try {
      await roomRef.current.localParticipant.setMicrophoneEnabled(!muted);
      console.log(`Microphone ${muted ? "muted" : "unmuted"}`);
    } catch (error) {
      console.error("Error toggling mute:", error);
      throw error;
    }
  }, []);

  return {
    room: roomRef.current,
    connect,
    disconnect,
    toggleMicrophone,
  };
}
